import { useEffect, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  width?: number
  closeOnOverlay?: boolean
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 600,
  closeOnOverlay = true,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 transition-opacity duration-base ease-out',
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
      )}
      aria-hidden={!open}
    >
      <div
        className="absolute inset-0 bg-neutral-900/35 backdrop-blur-[1px]"
        onClick={() => closeOnOverlay && onClose()}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        className={cn(
          'absolute right-0 top-0 h-full bg-bg-surface shadow-lg flex flex-col',
          'transition-transform duration-slow ease-emphasis',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
        style={{ width: Math.min(width, typeof window !== 'undefined' ? window.innerWidth - 24 : width) }}
      >
        <header className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border-subtle">
          <div className="min-w-0">
            <h2 id="drawer-title" className="font-display text-lg font-bold text-fg-1 truncate">
              {title}
            </h2>
            {description && <p className="text-sm text-fg-3 mt-0.5">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-3 hover:text-fg-1 p-1 -m-1 rounded transition-colors"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="border-t border-border-subtle px-6 py-4 bg-bg-subtle">{footer}</div>}
      </aside>
    </div>
  )
}
